import { SmlsInputText } from '@smiles/smiles-ui-kit-react';
import { useMemberData } from 'pages/RedoRegisterTangie/context/MemberDataContext';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountHelpers } from 'services/core/config';
import { getGeocoding, getZipCode } from 'services/core/services';

export function ZipcodeInput() {
  const NUMBER_REGEX = /[^0-9-]+|-(.*)-/g;
  const FOREIGN_REGEX = /[^a-zA-Z0-9-\s]+|-(.*)-/g;

  const { t } = useTranslation('redo-register-v2');
  const { handler, setFormEdit, formData } = useMemberData();

  const [value, setValue] = useState('');
  const [forceError, setForceError] = useState(false);
  const [shouldForceError, setShouldForceError] = useState(false);

  const setMask = useCallback(
    (zipcode: string): string => {
      return zipcode.replace(/^(\d{5})(\d)/, '$1-$2');
    },
    [value],
  );

  const unsetMask = useCallback(
    (zipcode: string): string => {
      return zipcode.replace(/[-.]/gi, '');
    },
    [value],
  );

  const zipcodeCheck = useCallback(
    async (zipcode: string) => {
      try {
        const response = await getZipCode(zipcode);

        if (!response?.data) {
          throw new Error('[SIEBEL] Could not possible verify this zipcode');
        }

        const geoResponse = await getGeocoding(zipcode);

        const address = geoResponse.data.lstAddress[0];

        const ufList = AccountHelpers.Constants.UF;
        const currentState = ufList[response.data.state];
        const siebelCountry = response.data.country;

        handler({
          streetName: address.street?.longName || response.data.streetName || '',
          city: address.city?.longName || response.data.city || '',
          state: currentState,
          country: siebelCountry,
          zipCode: zipcode,
        });
      } catch (e) {
        handler({ zipCode: '' });
        setForceError(true);
        console.log('[callFirstZipcodeCheck]', e);
      }
    },
    [value],
  );

  const foreignZipcodeCheck = useCallback(
    async (zipcode: string) => {
      const ufList = AccountHelpers.Constants.UF;
      let currentState;
      let siebelCountry;

      try {
        const response = await getZipCode(zipcode);

        const geoSearch = formData.country ? `${formData.country}, ${zipcode}` : zipcode;
        const geoResponse = await getGeocoding(geoSearch);

        const address = geoResponse.data.lstAddress[0];

        if (response?.data) {
          currentState = ufList[response.data.state];
          siebelCountry = response.data.country;
        }

        handler({
          streetName: address.street?.longName || response.data?.streetName || '',
          city: address.city?.longName || response.data?.city || '',
          state: currentState || address.federatedUnit?.longName || '',
          country: address.country?.longName || siebelCountry || '',
          zipCode: zipcode || address.zipCode?.longName || '',
        });
      } catch (e) {
        handler({ zipCode: '' });
        setForceError(true);
        console.log('[callFirstZipcodeCheck]', e);
      }
    },
    [value],
  );

  const handleZipcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const eventValue = e.target.value;
    const rawZipcode = eventValue.replace(/\D/g, '');
    const zipcodeLength = rawZipcode.length;

    if (formData.isForeign) {
      setValue(eventValue);
      return;
    }

    const maskedZipcode = setMask(eventValue);
    setShouldForceError(true);
    setFormEdit();

    handler({ zipCode: '' });
    setForceError(false);

    setValue(maskedZipcode);

    if (zipcodeLength < 8) {
      return;
    }

    const unmaskedZipcode = unsetMask(eventValue);
    zipcodeCheck(unmaskedZipcode);
  };

  const handleValidateForeign = (inputText: string): boolean => {
    foreignZipcodeCheck(inputText);
    return true;
  };

  return formData.isForeign ? (
    <SmlsInputText
      id="first-access-address-zipcode-input"
      data-testid="first-access-address-zipcode-input"
      textLabel={t('labels.zipcode_foreign')}
      onChange={handleZipcodeChange}
      inputMode="text"
      debouncedTime={2000}
      validate={handleValidateForeign}
      value={value}
      errorMessage={t('errors.zipcode')}
      forceError={shouldForceError && (value.length < 9 || forceError)}
      regex={FOREIGN_REGEX}
    />
  ) : (
    <SmlsInputText
      id="first-access-address-zipcode-input"
      data-testid="first-access-address-zipcode-input"
      textLabel={t('labels.zipcode')}
      onChange={handleZipcodeChange}
      inputMode="numeric"
      maxLength={9}
      debouncedTime={1000}
      value={value}
      errorMessage={t('errors.zipcode')}
      forceError={shouldForceError && (value.length < 9 || forceError)}
      regex={NUMBER_REGEX}
    />
  );
}
